import React, { useEffect, useRef, useState, useCallback } from 'react';
import GUI, { Controller } from 'lil-gui';
import useSimulationStore from '../store/simulationStore';
// Remove unused legacy types if setControls is removed
// import { SimulationControls, ChemicalSpeciesDefinition, ReactionDefinition, ReactionComponent, SimulationPreset } from '../types/simulation'; 
import { MathUtils } from 'three';
import { v4 as uuidv4 } from 'uuid';

// Import necessary types from store/chemistry
import { PresetManifestItem, ActiveChemistryConfiguration, ActiveReactionConfig } from '../store/simulationStore';
import { Species, Reaction } from '../types/chemistry';

// --- REMOVE Helper: Create Default Species --- (No longer needed if config is managed by store actions)
/*
const createDefaultSpecies = (index: number): ChemicalSpeciesDefinition => {
    // ...
};
*/

// --- REMOVE Helper: Create Default Reaction --- (No longer needed if config is managed by store actions)
/*
const createDefaultReaction = (): ReactionDefinition => {
    // ...
};
*/

// Hook to populate a lil-gui instance with Chemistry settings
export const useChemistryGui = (gui: GUI | null) => {
    // Select the new ready flag
    const isChemistryConfigReady = useSimulationStore(state => state.isChemistryConfigReady);
    // Select other needed state and actions directly here
    const {
        availablePresets,
        availableReactions,
        availableSpecies,
        activeChemistryConfig,
        loadPresetIntoActiveConfig,
        addReactionToActiveConfig,
        removeReactionFromActiveConfig,
        updateActiveReactionCoefficients,
        updateActiveReactionInitialCount,
        addSpeciesToActiveReaction,
        removeSpeciesFromActiveReaction,
        resetSimulation,
        uiFeatures
    } = useSimulationStore(state => ({
        availablePresets: state.availablePresets,
        availableReactions: state.availableReactions,
        availableSpecies: state.availableSpecies,
        activeChemistryConfig: state.activeChemistryConfig,
        loadPresetIntoActiveConfig: state.loadPresetIntoActiveConfig,
        addReactionToActiveConfig: state.addReactionToActiveConfig,
        removeReactionFromActiveConfig: state.removeReactionFromActiveConfig,
        updateActiveReactionCoefficients: state.updateActiveReactionCoefficients,
        updateActiveReactionInitialCount: state.updateActiveReactionInitialCount,
        addSpeciesToActiveReaction: state.addSpeciesToActiveReaction,
        removeSpeciesFromActiveReaction: state.removeSpeciesFromActiveReaction,
        resetSimulation: state.resetSimulation,
        uiFeatures: state.uiFeatures
    }));

    // --- Refs for GUI Elements ---
    const presetControllerRef = useRef<Controller | null>(null);
    const reactionsFolderRef = useRef<GUI | null>(null);
    const addReactionControllerRef = useRef<Controller | null>(null);
    const coefficientControllersRef = useRef<{ [key: string]: Controller }>({}).current;
    const countControllersRef = useRef<{ [key: string]: Controller }>({}).current;
    const reactionFolderRefs = useRef<{ [key: string]: GUI }>({}).current;
    const speciesFolderRefs = useRef<{ [key: string]: GUI }>({}).current;

    // --- Local state for GUI interaction ---
    const presetGuiState = useRef({ selectedFile: '' }).current;
    const addSpeciesState = useRef<{ [reactionId: string]: { reactant: string, product: string } }>({}).current;
    const addReactionState = useRef({ selectedReactionId: '' }).current;

    // --- Button Actions ---
    const guiActions = {
        addReactionHandler: (reactionId: string) => {
            if (reactionId) {
                addReactionToActiveConfig(reactionId);
            } else {
                console.warn("[useChemistryGui] No reaction selected to add.")
            }
        },
        addReactantHandler: (reactionId: string) => {
            const speciesId = addSpeciesState[reactionId]?.reactant;
            if (speciesId) {
                addSpeciesToActiveReaction(reactionId, speciesId, 'reactants');
                addSpeciesState[reactionId].reactant = "";
                // TODO: Force dropdown update?
            } else { console.warn("[useChemistryGui] No reactant species selected.") }
        },
        addProductHandler: (reactionId: string) => {
            const speciesId = addSpeciesState[reactionId]?.product;
            if (speciesId) {
                addSpeciesToActiveReaction(reactionId, speciesId, 'products');
                addSpeciesState[reactionId].product = "";
                // TODO: Force dropdown update?
            } else { console.warn("[useChemistryGui] No product species selected.") }
        },
        applyAndReset: () => {
            // Trigger simulation reset with current config
            resetSimulation();
        }
    };

    // --- Main GUI Population Effect ---
    // Add activeChemistryConfig to dependencies for more robust updates
    useEffect(() => {


        if (!gui || uiFeatures.useNewControls) {
            return; // Cannot do anything without the GUI instance or if new controls are enabled
        }

        // --- Always Clear GUI on Effect Run --- 
        [...gui.controllers].forEach(c => c.destroy());
        [...gui.folders].forEach(f => f.destroy());
        // Clear refs
        presetControllerRef.current = null;
        reactionsFolderRef.current = null;
        addReactionControllerRef.current = null;
        Object.keys(coefficientControllersRef).forEach(key => delete coefficientControllersRef[key]);
        Object.keys(countControllersRef).forEach(key => delete countControllersRef[key]);
        Object.keys(reactionFolderRefs).forEach(key => delete reactionFolderRefs[key]);
        Object.keys(speciesFolderRefs).forEach(key => delete speciesFolderRefs[key]);


        // === Add Apply/Reset Button FIRST (Always available) ===
        gui.add(guiActions, 'applyAndReset').name('Apply Config & Reset Sim');

        // --- Build Preset Dropdown (Always available if presets exist) ---
        const presetOptions: { [key: string]: string } = { "-- Select Preset --": "" };
        (availablePresets || []).forEach(preset => { presetOptions[preset.name] = preset.file; });
        if (Object.keys(presetOptions).length > 1) {
            presetControllerRef.current = gui.add(presetGuiState, 'selectedFile', presetOptions)
                .name("Load Preset")
                .onChange((fileName: string) => {
                    if (fileName) {

                        loadPresetIntoActiveConfig(fileName); // This action now sets the ready flag eventually
                        const ctrl = presetControllerRef.current;
                        if (ctrl) {
                            presetGuiState.selectedFile = "";
                            ctrl.updateDisplay();
                        }
                    }
                });
        }

        // --- Build ADD REACTION section (Always available if reactions exist) ---
        const addReactionFolder = gui.addFolder('Add New Reaction');
        const reactionOptions: { [key: string]: string } = { "-- Select Reaction Definition --": "" };
        (availableReactions || []).forEach(reaction => {
            reactionOptions[reaction.description || `ID: ${reaction.id}`] = reaction.id;
        });
        if (Object.keys(reactionOptions).length > 1) {
            addReactionControllerRef.current = addReactionFolder.add(addReactionState, 'selectedReactionId', reactionOptions).name('Select Reaction');
            addReactionFolder.add({
                add: () => {
                    const selectedId = addReactionState.selectedReactionId;
                    guiActions.addReactionHandler(selectedId);
                    addReactionState.selectedReactionId = "";
                    addReactionControllerRef.current?.updateDisplay();
                }
            }, 'add').name('Add to Configuration');
        } else {
            addReactionFolder.add({ msg: "No reaction definitions loaded/available." }, "msg").disable();
        }


        // --- Build ACTIVE REACTIONS section only if ready ---
        const reactionsFolder = gui.addFolder('Active Reactions');
        reactionsFolderRef.current = reactionsFolder;

        // Use the config passed into the effect via closure, reflecting state when effect ran
        if (isChemistryConfigReady) {


            const currentActiveConfig = activeChemistryConfig;
            const currentAvailableSpecies = availableSpecies;
            const currentAvailableReactions = availableReactions;



            // --- ADDED DEFENSIVE CHECK ---
            if (!currentActiveConfig || !Array.isArray(currentActiveConfig.reactions) || !Array.isArray(currentAvailableSpecies)) {

                reactionsFolder.add({ msg: "Inconsistent state: Config ready but data missing." }, "msg").disable();
            } else {

                const speciesNameMap = new Map<string, string>();
                (currentAvailableSpecies || []).forEach(s => speciesNameMap.set(s.id, s.name));
                const getSpeciesName = (id: string): string => speciesNameMap.get(id) || `Unknown (${id.substring(0, 4)})`;

                const speciesOptions: { [key: string]: string } = { "-- Select Species --": "" };
                (currentAvailableSpecies || []).forEach(s => { speciesOptions[s.name] = s.id; });

                if (!currentActiveConfig || currentActiveConfig.reactions.length === 0) {
                    reactionsFolder.add({ msg: "No reactions configured." }, "msg").disable(); // Simplified message

                } else {

                    currentActiveConfig.reactions.forEach((reactionConfig: ActiveReactionConfig, index: number) => {


                        const reactionBaseDef = currentAvailableReactions.find(r => r.id === reactionConfig.reactionDefId);
                        const reactionTitle = reactionBaseDef?.description || `Reaction (ID: ${reactionConfig.id.substring(0, 4)})`;
                        const reactionFolder = reactionsFolder.addFolder(reactionTitle);
                        reactionFolderRefs[reactionConfig.id] = reactionFolder;

                        // Create proxies using the current reactionConfig
                        const coeffProxy = {
                            reactants: { ...reactionConfig.reactantsCoeff },
                            products: { ...reactionConfig.productsCoeff }
                        };
                        const countsProxy = { ...reactionConfig.initialCounts };

                        // Reactants
                        const reactantsFolder = reactionFolder.addFolder('Reactants');
                        Object.entries(coeffProxy.reactants).forEach(([speciesId, coefficient]) => {
                            const speciesName = getSpeciesName(speciesId);
                            const folderTitle = `${speciesName} (ID: ${speciesId.substring(0, 4)})`;
                            const folderKey = `${reactionConfig.id}-reactant-${speciesId}`;
                            const reactantCtrlFolder = reactantsFolder.addFolder(folderTitle);
                            speciesFolderRefs[folderKey] = reactantCtrlFolder;
                            const controllerKey = `${reactionConfig.id}-reactant-${speciesId}`;
                            const controller = reactantCtrlFolder.add(coeffProxy.reactants, speciesId, 0, 10, 1).name('Coefficient')
                                .onFinishChange((newCoeffFromGui: number) => updateActiveReactionCoefficients(reactionConfig.id, speciesId, 'reactants', newCoeffFromGui));
                            coefficientControllersRef[controllerKey] = controller;
                            reactantCtrlFolder.add({ remove: () => removeSpeciesFromActiveReaction(reactionConfig.id, speciesId, 'reactants') }, 'remove').name('Remove');
                            reactantCtrlFolder.title(folderTitle); // Explicit update
                        });
                        if (!addSpeciesState[reactionConfig.id]) addSpeciesState[reactionConfig.id] = { reactant: "", product: "" };
                        reactantsFolder.add(addSpeciesState[reactionConfig.id], 'reactant', speciesOptions).name('Add Species');
                        reactantsFolder.add({ add: () => guiActions.addReactantHandler(reactionConfig.id) }, 'add').name('Add Reactant');

                        // Products
                        const productsFolder = reactionFolder.addFolder('Products');
                        Object.entries(coeffProxy.products).forEach(([speciesId, coefficient]) => {
                            const speciesName = getSpeciesName(speciesId);
                            const folderTitle = `${speciesName} (ID: ${speciesId.substring(0, 4)})`;
                            const folderKey = `${reactionConfig.id}-product-${speciesId}`;
                            const productCtrlFolder = productsFolder.addFolder(folderTitle);
                            speciesFolderRefs[folderKey] = productCtrlFolder;
                            const controllerKey = `${reactionConfig.id}-product-${speciesId}`;
                            const controller = productCtrlFolder.add(coeffProxy.products, speciesId, 0, 10, 1).name('Coefficient')
                                .onFinishChange((newCoeffFromGui: number) => updateActiveReactionCoefficients(reactionConfig.id, speciesId, 'products', newCoeffFromGui));
                            coefficientControllersRef[controllerKey] = controller;
                            productCtrlFolder.add({ remove: () => removeSpeciesFromActiveReaction(reactionConfig.id, speciesId, 'products') }, 'remove').name('Remove');
                            productCtrlFolder.title(folderTitle); // Explicit update
                        });
                        if (!addSpeciesState[reactionConfig.id]) addSpeciesState[reactionConfig.id] = { reactant: "", product: "" };
                        productsFolder.add(addSpeciesState[reactionConfig.id], 'product', speciesOptions).name('Add Species');
                        productsFolder.add({ add: () => guiActions.addProductHandler(reactionConfig.id) }, 'add').name('Add Product');

                        // Initial Counts
                        const countsFolder = reactionFolder.addFolder('Initial Counts');
                        const involvedSpeciesIds = new Set([...Object.keys(coeffProxy.reactants), ...Object.keys(coeffProxy.products)]);
                        involvedSpeciesIds.forEach(speciesId => {
                            const speciesName = getSpeciesName(speciesId);
                            if (countsProxy[speciesId] === undefined) countsProxy[speciesId] = 0;
                            const controllerKey = `${reactionConfig.id}-count-${speciesId}`;
                            const controller = countsFolder.add(countsProxy, speciesId, 0, 1000, 1).name(speciesName)
                                .onFinishChange((newCountFromGui: number) => updateActiveReactionInitialCount(reactionConfig.id, speciesId, newCountFromGui));
                            countControllersRef[controllerKey] = controller;
                            controller.name(speciesName); // Explicit update
                        });

                        // Remove Reaction Button
                        reactionFolder.add({ remove: () => removeReactionFromActiveConfig(reactionConfig.id) }, 'remove').name('❌ Remove This Reaction');
                    });
                }
            }
        } else {

            reactionsFolder.add({ msg: "Configuration not loaded or modified." }, "msg").disable();
        }

        // Cleanup function - clearing happens at START of next effect run
        return () => { };

        // Dependencies: use stable references - activeChemistryConfig.reactions.length and
        // availableSpecies/Reactions/Presets.length as proxies for changes
    }, [gui, isChemistryConfigReady,
        activeChemistryConfig.reactions.length,
        availablePresets.length,
        availableReactions.length,
        availableSpecies.length,
        // Include actual action functions that are stable
        loadPresetIntoActiveConfig,
        addReactionToActiveConfig,
        removeReactionFromActiveConfig,
        updateActiveReactionCoefficients,
        updateActiveReactionInitialCount,
        addSpeciesToActiveReaction,
        removeSpeciesFromActiveReaction,
        resetSimulation
    ]);

}; 